'use client';

import { FunctionalIcon } from '@/app/Components/FunctionalIcon';
import React, { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import dynamic from 'next/dynamic';
import { ArrowTopRightOnSquareIcon, MagnifyingGlassIcon, SparklesIcon, TrashIcon } from '@heroicons/react/24/outline';
import { findProfessorRatings } from '@/app/lib/professorData';
import { isSemesterId } from '@/app/lib/semesters';
import { useCoursePeek } from '@/app/Components/CoursePeek/CoursePeekProvider';
import { markOpenedInPlace } from '@/app/lib/inPlaceNavigation';
import { normalize } from '@/app/services/degreeRequirementService';
import { usePlanner, userListMappingName, type PlannerOverlay, type UserCourseList } from './plannerContext';
import {
  earliestReadyOrder,
  formatCode,
  offeredSeasons,
  prerequisiteClauses,
  requirementsForCourse,
  shortCode,
  type PrereqClause,
} from './plannerModel';
import { CreditMeter, focusMovedCard, PlannerOverlayShell, ProgramDot, SectionLabel, SlotRow, StatusPill, TermChips } from './PlannerPrimitives';
const CompletionOptions = dynamic(() => import('./CompletionOptions'));

// ─── Grade evidence (Spring 2026 NB course-level snapshot, loaded once on demand) ─────────

type GradeRecord = { baseId: string; grades: Record<string, number>; totalOutcomes: number };
let gradeIndexPromise: Promise<Map<string, GradeRecord>> | null = null;
function loadGradeIndex(): Promise<Map<string, GradeRecord>> {
  if (!gradeIndexPromise) {
    gradeIndexPromise = fetch('/data/grade-distributions/spring-2026-new-brunswick.json')
      .then((response) => { if (!response.ok) throw new Error('grade evidence unavailable'); return response.json(); })
      .then((data: { records: GradeRecord[] }) => {
        const map = new Map<string, GradeRecord>();
        data.records.forEach((record) => {
          if (!record.totalOutcomes) return;
          const existing = map.get(record.baseId);
          if (!existing) { map.set(record.baseId, { ...record, grades: { ...record.grades } }); return; }
          existing.totalOutcomes += record.totalOutcomes;
          Object.entries(record.grades).forEach(([grade, count]) => { existing.grades[grade] = (existing.grades[grade] || 0) + count; });
        });
        return map;
      })
      .catch((error) => { gradeIndexPromise = null; throw error; });
  }
  return gradeIndexPromise;
}

function GradeTile({ code }: { code: string }) {
  const [record, setRecord] = useState<GradeRecord | null | undefined>(undefined);
  useEffect(() => {
    let active = true;
    loadGradeIndex().then((map) => { if (active) setRecord(map.get(normalize(code)) || null); }).catch(() => { if (active) setRecord(null); });
    return () => { active = false; };
  }, [code]);
  if (!record) return null;
  const share = (keys: string[]) => keys.reduce((sum, key) => sum + (record.grades[key] || 0), 0) / record.totalOutcomes;
  const a = Math.round(share(['A']) * 100);
  const dfw = Math.round(share(['D', 'F', 'W']) * 100);
  return (
    <div className="flex flex-col gap-1.5 rounded-control border border-[var(--ss-border)] px-2.5 py-2">
      <span className="text-[11px] text-[var(--ss-text-muted)]">Grades · Spring 2026 NB</span>
      <span className="flex h-2 overflow-hidden rounded-full bg-[var(--ss-surface-subtle)]" aria-hidden="true">
        <span className="bg-[var(--ss-positive)]" style={{ width: `${a}%` }} />
        <span className="bg-[var(--ss-border)]" style={{ width: `${Math.max(0, 100 - a - dfw)}%` }} />
        <span className="bg-[var(--ss-critical)]" style={{ width: `${dfw}%` }} />
      </span>
      <span className="text-[11px] tabular-nums text-[var(--ss-text-muted)]"><b className="text-[var(--ss-text)]">{a}%</b> A · {dfw}% D/F/W · {record.totalOutcomes.toLocaleString()} grades</span>
    </div>
  );
}

function ProfessorTile({ instructors }: { instructors: string[] }) {
  const ratings = useMemo(() => findProfessorRatings(instructors).filter((r) => r.numRatings > 0), [instructors]);
  if (!ratings.length) return null;
  const avg = (key: 'avgRating' | 'avgDifficulty') => ratings.reduce((sum, r) => sum + r[key], 0) / ratings.length;
  return (
    <div className="flex flex-col gap-1 rounded-control border border-[var(--ss-border)] px-2.5 py-2">
      <span className="text-[11px] text-[var(--ss-text-muted)]">Listed instructors · RMP avg</span>
      <span className="text-[17px] font-bold tabular-nums leading-none">{avg('avgRating').toFixed(1)}<span className="text-[11px] font-medium text-[var(--ss-text-muted)]"> / 5</span></span>
      <span className="text-[11px] text-[var(--ss-text-muted)]">Difficulty {avg('avgDifficulty').toFixed(1)} · {ratings.length} instructor{ratings.length === 1 ? '' : 's'}</span>
    </div>
  );
}

// ─── Course peek ─────────────────────────────────────────────────────────────────────────

const clauseTone: Record<PrereqClause['status'], 'earned' | 'planned' | 'missing' | 'neutral'> = {
  earned: 'earned', planned: 'planned', late: 'missing', missing: 'missing', unknown: 'neutral',
};

function clauseLabel(clause: PrereqClause): string {
  if (clause.status === 'earned') return 'Earned';
  if (clause.status === 'planned') return clause.termName || 'Planned';
  if (clause.status === 'late') return `Too late · ${clause.termName}`;
  if (clause.status === 'unknown') return 'Check catalog';
  return 'Not in plan';
}

/** A planned term opens that term's catalog when Scarlet Sync has it; otherwise the default term. */
function catalogTermFor(termName?: string) {
  const id = termName?.trim().toLowerCase().replace(/\s+/g, '-');
  return id && isSemesterId(id) ? id : undefined;
}

export function CoursePeek({ code, anchor }: { code: string; anchor: HTMLElement | null }) {
  const planner = usePlanner();
  const peek = useCoursePeek();
  const { index, completed, placement, terms, programs, issuesByCode, nameOf, creditsOf, actions, close, open } = planner;
  const norm = normalize(code);
  const course = index.byCode.get(norm);
  const where = placement.get(norm);
  const earned = completed.has(norm);
  const order = where ? where.order : Number.POSITIVE_INFINITY;
  const clauses = prerequisiteClauses(norm, order, index, completed, placement);
  const counts = requirementsForCourse(norm, programs);
  const programCount = new Set(counts.map((r) => r.degreeId)).size;
  const seasons = offeredSeasons(norm);
  const term = where ? terms[where.order] : null;
  const offSeason = Boolean(term?.season && seasons && !seasons.includes(term.season));
  const relevant = new Set([...placement.keys(), ...programs.flatMap((p) => p.requirements.flatMap((r) => r.pool))]);
  const unlocks = (index.dependents.get(norm) || []).filter((dep) => relevant.has(dep)).slice(0, 10);
  const issues = issuesByCode.get(norm) || [];
  const readyOrder = !where && !earned ? earliestReadyOrder(norm, terms, index, completed, placement) : undefined;

  return (
    <PlannerOverlayShell anchor={anchor} label={`${nameOf(norm)} details`} onClose={close}>
      <div className="flex items-start justify-between gap-3 pr-6">
        <div className="min-w-0">
          <h3 className="text-[15px] font-bold leading-snug">{nameOf(norm)}</h3>
          <p className="mt-0.5 font-mono text-xs text-[var(--ss-text-muted)]">{formatCode(norm)}</p>
        </div>
        {earned ? <StatusPill tone="earned">Earned</StatusPill> : where ? <StatusPill tone="planned">{where.termName}</StatusPill> : <StatusPill tone="neutral">Not in plan</StatusPill>}
      </div>

      <div className="flex flex-wrap gap-1.5 text-[11.5px]">
        <span className="rounded-full bg-[var(--ss-surface-subtle)] px-2 py-0.5 ring-1 ring-inset ring-[var(--ss-border)]"><b>{creditsOf(norm) || '?'}</b> credits</span>
        <span className="rounded-full bg-[var(--ss-surface-subtle)] px-2 py-0.5 ring-1 ring-inset ring-[var(--ss-border)]">{seasons ? <>Usually <b>{seasons.join(' · ')}</b></> : 'Offering pattern unknown'}</span>
        {(course?.coreRequirements || []).slice(0, 4).map((core) => (
          <span key={String(core)} className="rounded-full bg-[var(--ss-info-bg)] px-2 py-0.5 font-semibold text-[var(--ss-info)]">{String(core)}</span>
        ))}
      </div>

      {offSeason ? <p className="rounded-control bg-[var(--ss-warning-bg)] px-2.5 py-1.5 text-xs text-[var(--ss-warning)]">Not typically offered in {term?.season}. Check the schedule before relying on this term.</p> : null}
      {issues.map((issue, i) => (
        <p key={i} className="rounded-control bg-[var(--ss-critical-bg)] px-2.5 py-1.5 text-xs text-[var(--ss-critical)]">{issue.issue}</p>
      ))}

      <div className="flex flex-col gap-1.5">
        <SectionLabel>Counts toward</SectionLabel>
        {counts.length ? counts.map((req) => (
          <div key={req.key} className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-2"><ProgramDot index={req.programIndex} /><span className="truncate">{req.title}</span></span>
            <span className="max-w-[40%] truncate text-[11px] text-[var(--ss-text-muted)]">{req.degreeName}</span>
          </div>
        )) : <p className="text-xs text-[var(--ss-text-muted)]">Not part of the programs in this plan. It still adds to total credits.</p>}
        {programCount > 1 ? <p className="text-[11px] text-[var(--ss-text-muted)]">Listed in more than one program. Limits on reusing a course aren&apos;t checked here, so confirm with an advisor.</p> : null}
      </div>

      {clauses.length ? (
        <div className="flex flex-col gap-1.5">
          <SectionLabel>Prerequisites in this plan</SectionLabel>
          {clauses.map((clause, i) => (
            <div key={i} className="flex items-center justify-between gap-2">
              <span className="min-w-0 truncate font-mono text-xs">{clause.options.map(shortCode).join(' or ')}</span>
              <span className="flex items-center gap-1.5">
                <StatusPill tone={clauseTone[clause.status]}>{clauseLabel(clause)}</StatusPill>
                {(clause.status === 'missing' || clause.status === 'late') && where && where.order > 0 ? (
                  <button type="button" className="text-[11px] font-semibold text-[var(--ss-brand)] hover:underline" onClick={() => actions.addCourse(terms[where.order - 1].number, clause.options[0])}>
                    Add to {terms[where.order - 1].name.replace(/20(\d\d)/, '’$1')}
                  </button>
                ) : null}
              </span>
            </div>
          ))}
          <p className="text-[11px] text-[var(--ss-text-muted)]">Read from the catalog&apos;s prerequisite text, not an official registrar check.</p>
        </div>
      ) : null}

      {unlocks.length ? (
        <div className="flex flex-col gap-1.5">
          <SectionLabel>Unlocks</SectionLabel>
          <div className="flex flex-wrap gap-1.5">
            {unlocks.map((dep) => (
              <button key={dep} type="button" data-planner-keep-overlay onClick={(e) => open({ kind: 'course', code: dep, anchor: anchor?.isConnected ? anchor : e.currentTarget })} title={nameOf(dep)}
                className={`rounded-full px-2 py-0.5 font-mono text-[11px] ring-1 ring-inset hover:ring-[var(--ss-brand)] ${placement.has(dep) ? 'bg-[var(--ss-warning-bg)] text-[var(--ss-warning)] ring-transparent' : 'bg-[var(--ss-surface-subtle)] ring-[var(--ss-border)]'}`}>
                {shortCode(dep)}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-2 empty:hidden">
        <GradeTile code={norm} />
        <ProfessorTile instructors={course?.instructors || []} />
      </div>

      {!earned ? (
        <div className="flex flex-col gap-1.5">
          <SectionLabel>{where ? 'Move to' : 'Add to'}</SectionLabel>
          <TermChips
            currentSemesterNumber={where?.semesterNumber}
            readyOrder={readyOrder}
            onPick={(semesterNumber) => {
              if (where) actions.moveItem(where.semesterNumber, where.index, semesterNumber);
              else actions.addCourse(semesterNumber, norm);
            }}
          />
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--ss-border)] pt-2.5 text-xs font-semibold">
        <span className="flex gap-3">
          <Link href={`/explore?course=${norm}`} onClick={(event) => {
            if (!peek || event.metaKey || event.ctrlKey) return;
            event.preventDefault();
            markOpenedInPlace(event);
            close();
            peek.openCourse({
              courseId: norm,
              semester: catalogTermFor(where?.termName),
              context: {
                label: 'In your degree plan',
                facts: [
                  { label: 'Status', value: earned ? 'Earned' : where ? `Planned for ${where.termName}` : 'Not in plan' },
                  ...(counts.length ? [{ label: 'Counts toward', value: counts.map((req) => req.title).join(', ') }] : []),
                  ...(issues.length ? [{ label: 'Plan issues', value: String(issues.length) }] : []),
                ],
                note: offSeason ? `Usually offered ${seasons?.join(' · ')}, not in the term it's planned for.` : undefined,
              },
            });
          }} className="inline-flex items-center gap-1 text-[var(--ss-brand)] hover:underline">Course details <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" /></Link>
          <button type="button" className="inline-flex items-center gap-1 text-[var(--ss-text-muted)] hover:text-[var(--ss-text)]" onClick={() => { actions.askAboutCourse(norm, where?.termName); close(); }}><SparklesIcon className="h-3.5 w-3.5" />Ask</button>
        </span>
        {where ? (
          <button type="button" className="inline-flex items-center gap-1 text-[var(--ss-text-muted)] hover:text-[var(--ss-critical)]" onClick={() => { actions.removeItem(where.semesterNumber, where.index); close(); }}><TrashIcon className="h-3.5 w-3.5" />Remove</button>
        ) : null}
      </div>
    </PlannerOverlayShell>
  );
}

// ─── Placeholder (course-list item) peek ─────────────────────────────────────────────────

export function ListPeek({ semesterNumber, index: itemIndex, anchor }: { semesterNumber: number; index: number; anchor: HTMLElement | null }) {
  const { terms, programs, actions, close, listLabel, listCredits, nameOf } = usePlanner();
  const term = terms.find((t) => t.number === semesterNumber);
  const entry = term?.items.find((i) => i.index === itemIndex);
  if (!term || !entry || entry.item.kind !== 'course_list') return null;
  const item = entry.item;
  const reqs = programs.flatMap((p) => p.requirements.filter((r) => r.listNames.includes(item.mappingName)));
  return (
    <PlannerOverlayShell anchor={anchor} label={`${item.label} placeholder`} onClose={close}>
      <div className="pr-6">
        <h3 className="text-[15px] font-bold leading-snug">{item.label || listLabel(item.mappingName)}</h3>
        <p className="mt-0.5 text-xs text-[var(--ss-text-muted)]">Placeholder in {term.name} · about {listCredits(item.mappingName)} cr{(item.quantity || 1) > 1 ? ` × ${item.quantity}` : ''}</p>
      </div>
      {item.selectedCourseCode ? <p className="text-xs">Chosen: <b>{nameOf(normalize(item.selectedCourseCode))}</b></p> : null}
      {reqs.length ? (
        <div className="flex flex-col gap-1.5">
          <SectionLabel>Holds a spot for</SectionLabel>
          {reqs.map((req) => <div key={req.key} className="flex items-center gap-2"><ProgramDot index={req.programIndex} /><span>{req.title}</span></div>)}
        </div>
      ) : null}
      <div className="flex flex-col gap-1.5">
        <SectionLabel>Move to</SectionLabel>
        <TermChips currentSemesterNumber={semesterNumber} onPick={(to) => actions.moveItem(semesterNumber, itemIndex, to)} />
      </div>
      <div className="flex items-center justify-between border-t border-[var(--ss-border)] pt-2.5 text-xs font-semibold">
        <button type="button" data-autofocus className="rounded-control bg-[var(--ss-brand)] px-3 py-1.5 text-[var(--ss-brand-contrast)] hover:bg-[var(--ss-brand-strong)]" onClick={() => { actions.chooseListCourse(item, semesterNumber, itemIndex); close(); }}>Choose a course</button>
        <button type="button" className="inline-flex items-center gap-1 text-[var(--ss-text-muted)] hover:text-[var(--ss-critical)]" onClick={() => { actions.removeItem(semesterNumber, itemIndex); close(); }}><TrashIcon className="h-3.5 w-3.5" />Remove</button>
      </div>
    </PlannerOverlayShell>
  );
}

// ─── Slot resolver: fill an open requirement slot ───────────────────────────────────────

export function SlotResolver({ requirementKey, semesterNumber, anchor }: { requirementKey: string; semesterNumber?: number; anchor: HTMLElement | null }) {
  const { requirementByKey, completed, placement, terms, index, nameOf, creditsOf, actions, close, open, listLabel, listCredits } = usePlanner();
  const [query, setQuery] = useState('');
  const req = requirementByKey.get(requirementKey);
  const options = useMemo(() => {
    if (!req) return [];
    const q = query.trim().toLowerCase();
    return req.pool
      .filter((code) => !completed.has(code) && !placement.has(code))
      .filter((code) => !q || nameOf(code).toLowerCase().includes(q) || formatCode(code).includes(q) || shortCode(code).includes(q))
      .map((code) => ({ code, ready: earliestReadyOrder(code, terms, index, completed, placement) }))
      .sort((a, b) => (a.ready ?? 99) - (b.ready ?? 99) || a.code.localeCompare(b.code))
      .slice(0, 30);
  }, [req, query, completed, placement, terms, index, nameOf]);
  if (!req) return null;
  const targetTerm = semesterNumber !== undefined ? terms.find((t) => t.number === semesterNumber) : undefined;

  return (
    <PlannerOverlayShell anchor={anchor} label={`Fill ${req.title}`} onClose={close} width={380}>
      <div className="pr-6">
        <h3 className="text-[15px] font-bold leading-snug">{req.title}</h3>
        <p className="mt-0.5 flex items-center gap-1.5 text-xs text-[var(--ss-text-muted)]"><ProgramDot index={req.programIndex} />{req.degreeName}</p>
      </div>
      {req.mode === 'courses' ? <SlotRow slots={req.slots} size="lg" /> : <CreditMeter earned={req.creditsEarned} planned={req.creditsPlanned} needed={req.creditsNeeded} />}
      {req.description ? <p className="rounded-control bg-[var(--ss-surface-subtle)] px-2.5 py-2 text-xs leading-relaxed text-[var(--ss-text-muted)] ring-1 ring-inset ring-[var(--ss-border)]">{req.description}</p> : null}

      {req.pool.length > 6 ? (
        <label className="flex items-center gap-2 rounded-control border border-[var(--ss-border)] px-2.5 py-1.5 focus-within:border-[var(--ss-brand)]">
          <MagnifyingGlassIcon className="h-4 w-4 text-[var(--ss-text-muted)]" aria-hidden="true" />
          <input data-autofocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder={`Search ${req.pool.length} courses`} className="w-full bg-transparent text-sm outline-none placeholder:text-[var(--ss-text-muted)]" aria-label="Search courses in this requirement" />
        </label>
      ) : null}

      <div className="flex flex-col gap-2">
        <SectionLabel>{targetTerm ? `Choose a course for ${targetTerm.name}` : 'Choose a course'}</SectionLabel>
        {options.length ? options.map(({ code, ready }) => {
          const seasons = offeredSeasons(code);
          return (
            <div key={code} className="flex flex-col gap-1.5 rounded-control border border-[var(--ss-border)] p-2.5">
              <div className="flex items-start justify-between gap-2">
                <span className="text-[13px] font-semibold leading-snug">{nameOf(code)}</span>
                <span className="font-mono text-[11px] text-[var(--ss-text-muted)]">{shortCode(code)}</span>
              </div>
              <span className="text-[11.5px] text-[var(--ss-text-muted)]">
                {creditsOf(code) || '?'} cr{seasons ? ` · ${seasons.filter((s) => s !== 'Winter').join('/')}` : ''} · {ready === null ? 'prerequisites not in plan yet' : `ready by ${terms[ready].name}`}
              </span>
              {targetTerm ? (
                <button type="button" className="self-start rounded-control border border-[var(--ss-border)] px-2.5 py-1 text-xs font-semibold hover:border-[var(--ss-brand)] hover:text-[var(--ss-brand)]" onClick={() => { actions.addCourse(targetTerm.number, code); close(); }}>Add to {targetTerm.name}</button>
              ) : (
                <TermChips readyOrder={ready} onPick={(n) => { actions.addCourse(n, code); close(); }} />
              )}
            </div>
          );
        }) : <p className="text-xs text-[var(--ss-text-muted)]">{query ? 'No courses match that search.' : 'Every listed course is already earned or planned.'}</p>}
      </div>

      {req.listNames.length ? (
        <div className="flex flex-col gap-1.5">
          <SectionLabel>Or hold the spot and decide later</SectionLabel>
          {req.listNames.slice(0, 2).map((name) => (
            targetTerm ? (
              <button key={name} type="button" className="self-start rounded-control border border-dashed border-[var(--ss-text-muted)]/60 px-2.5 py-1 text-xs font-semibold hover:border-[var(--ss-brand)] hover:text-[var(--ss-brand)]" onClick={() => { actions.addList(targetTerm.number, name, listLabel(name)); close(); }}>
                {listLabel(name)} → {targetTerm.name} (about {listCredits(name)} cr)
              </button>
            ) : (
              <div key={name} className="flex flex-col gap-1">
                <span className="text-xs text-[var(--ss-text-muted)]">{listLabel(name)} · about {listCredits(name)} cr</span>
                <TermChips onPick={(n) => { actions.addList(n, name, listLabel(name)); close(); }} />
              </div>
            )
          ))}
        </div>
      ) : null}

      <YourLists semesterNumber={semesterNumber} pool={req.pool} />
      <button type="button" className="min-h-12 text-left text-sm font-semibold text-[var(--ss-brand)]" onClick={() => open({ kind: 'completion', requirementKey })}>Explore completion options for this requirement</button>
    </PlannerOverlayShell>
  );
}

// ─── Your lists: the student's saved course lists (courseListsApi) ──────────────────────

const LIST_PREVIEW = 6;

function TermSelect({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const { terms } = usePlanner();
  return (
    <label className="flex items-center gap-2 text-xs text-[var(--ss-text-muted)]">
      Add to
      <select value={value} onChange={(e) => onChange(Number(e.target.value))} className="min-h-8 rounded-control border border-[var(--ss-border)] bg-[var(--ss-surface)] px-2 text-xs font-semibold text-[var(--ss-text)]">
        {terms.map((t) => <option key={t.number} value={t.number}>{t.name}</option>)}
      </select>
    </label>
  );
}

function UserListRow({ list, semesterNumber, pool, onDone }: { list: UserCourseList; semesterNumber: number; pool?: Set<string>; onDone: () => void }) {
  const { completed, placement, nameOf, actions, listCredits } = usePlanner();
  const [expanded, setExpanded] = useState(false);
  const codes = pool ? list.classes.filter((code) => pool.has(code)) : list.classes;
  const shown = expanded ? codes : codes.slice(0, LIST_PREVIEW);
  return (
    <div className="flex flex-col gap-1.5 rounded-control border border-[var(--ss-border)] p-2.5">
      <div className="flex items-start justify-between gap-2">
        <span className="min-w-0">
          <span className="block text-[13px] font-semibold leading-snug">{list.name}</span>
          <span className="text-[11.5px] text-[var(--ss-text-muted)]">{pool ? `${codes.length} of ${list.classes.length} courses fit this requirement` : `${list.classes.length} course${list.classes.length === 1 ? '' : 's'}`}</span>
        </span>
        <button type="button" onClick={() => { actions.addUserListPlaceholder(semesterNumber, list); onDone(); }}
          title={pool ? 'Holds a spot; it counts toward the requirement once you choose a course' : 'Holds a spot; choose the course later'}
          className="flex-none rounded-control border border-dashed border-[var(--ss-text-muted)]/60 px-2 py-1 text-[11.5px] font-semibold hover:border-[var(--ss-brand)] hover:text-[var(--ss-brand)]">
          Hold a spot<span className="sr-only"> for {list.name}</span> · ~{listCredits(userListMappingName(list.id))} cr
        </button>
      </div>
      <div className="flex flex-wrap gap-1.5" role="group" aria-label={`Courses in ${list.name}`}>
        {shown.map((code) => {
          const where = placement.get(code);
          const done = completed.has(code);
          return (
            <button key={code} type="button" disabled={Boolean(where) || done} title={`${nameOf(code)}${done ? ' · earned' : where ? ` · in ${where.termName}` : ''}`}
              onClick={() => { actions.addCourse(semesterNumber, code); onDone(); }}
              className="rounded-full px-2 py-0.5 font-mono text-[11px] ring-1 ring-inset ring-[var(--ss-border)] hover:ring-[var(--ss-brand)] hover:text-[var(--ss-brand)] disabled:cursor-default disabled:opacity-50 disabled:hover:text-current disabled:hover:ring-[var(--ss-border)]">
              <span className="sr-only">Add {nameOf(code)}, </span>{shortCode(code)}{done ? <FunctionalIcon name="complete" className="ml-1" /> : where ? ' ·' : ''}
            </button>
          );
        })}
        {codes.length > LIST_PREVIEW ? (
          <button type="button" aria-expanded={expanded} onClick={() => setExpanded(!expanded)} className="px-1 text-[11px] font-semibold text-[var(--ss-text-muted)] hover:text-[var(--ss-text)]">
            {expanded ? 'Fewer' : `+${codes.length - LIST_PREVIEW} more`}
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Saved course lists. Each list can go into a term as a placeholder ("Hold a spot") or as one
 * chosen course. With `pool`, only lists that contain courses for that requirement are shown.
 */
export function YourLists({ semesterNumber, pool }: { semesterNumber?: number; pool?: string[] }) {
  const { userLists, userListsReady, terms, close } = usePlanner();
  const [pickedTerm, setPickedTerm] = useState<number | null>(null);
  const poolSet = useMemo(() => (pool ? new Set(pool) : undefined), [pool]);
  const lists = poolSet ? userLists.filter((list) => list.classes.some((code) => poolSet.has(code))) : userLists;
  const target = semesterNumber ?? pickedTerm ?? terms[0]?.number;
  const heading = pool ? 'From your lists' : null;

  let body: React.ReactNode;
  if (userListsReady === null) body = <p className="text-xs text-[var(--ss-text-muted)]">Loading your lists…</p>;
  else if (userListsReady === false) body = <p className="text-xs text-[var(--ss-text-muted)]">Your saved course lists couldn&apos;t be loaded. Sign in, or try again later.</p>;
  else if (!terms.length || target === undefined) body = <p className="text-xs text-[var(--ss-text-muted)]">Add a term to the plan first.</p>;
  else if (!lists.length) {
    if (pool) return null;
    body = <p className="text-xs text-[var(--ss-text-muted)]">No saved lists yet. <Link href="/course-lists" className="font-semibold text-[var(--ss-brand)] hover:underline">Make one in Course lists</Link>.</p>;
  } else {
    body = (
      <>
        {semesterNumber === undefined ? <TermSelect value={target} onChange={setPickedTerm} /> : null}
        {lists.map((list) => <UserListRow key={list.id} list={list} semesterNumber={target} pool={poolSet} onDone={close} />)}
      </>
    );
  }
  if (pool && userListsReady !== true) return null;
  return (
    <div className="flex flex-col gap-2">
      {heading ? <SectionLabel>{heading}</SectionLabel> : null}
      {body}
    </div>
  );
}

// ─── Move to (keyboard M on a card, or anywhere a card needs moving without drag) ────────

export function MovePanel({ semesterNumber, index: itemIndex, anchor }: { semesterNumber: number; index: number; anchor: HTMLElement | null }) {
  const { terms, nameOf, listLabel, actions, close } = usePlanner();
  const term = terms.find((t) => t.number === semesterNumber);
  const entry = term?.items.find((i) => i.index === itemIndex);
  if (!term || !entry) return null;
  const item = entry.item;
  const code = item.kind === 'course' ? normalize(item.code) : item.selectedCourseCode ? normalize(item.selectedCourseCode) : null;
  const title = code ? nameOf(code) : item.kind === 'course_list' ? item.label || listLabel(item.mappingName) : '';
  return (
    <PlannerOverlayShell anchor={anchor} label={`Move ${title}`} onClose={close} width={320}>
      <div className="pr-6">
        <h3 className="text-[15px] font-bold leading-snug">Move {title}</h3>
        <p className="mt-0.5 text-xs text-[var(--ss-text-muted)]">Now in {term.name}. Pick a term; Escape cancels.</p>
      </div>
      <MoveChips current={semesterNumber} onPick={(to) => { actions.moveItem(semesterNumber, itemIndex, to); close(); focusMovedCard(to, code); }} />
    </PlannerOverlayShell>
  );
}

function MoveChips({ current, onPick }: { current: number; onPick: (n: number) => void }) {
  const ref = React.useRef<HTMLDivElement>(null);
  // Focus the first term you can move to so the panel is usable straight from the keyboard. Deferred
  // a tick because the overlay shell focuses itself after its children mount.
  useEffect(() => {
    const id = window.setTimeout(() => ref.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus(), 0);
    return () => window.clearTimeout(id);
  }, []);
  return <div ref={ref}><TermChips currentSemesterNumber={current} onPick={onPick} /></div>;
}

// ─── Add to a term: catalog search and your saved lists ─────────────────────────────────

export function TermAddPanel({ semesterNumber, anchor }: { semesterNumber: number; anchor: HTMLElement | null }) {
  const { terms, index, completed, placement, nameOf, creditsOf, actions, close } = usePlanner();
  const [tab, setTab] = useState<'search' | 'lists'>('search');
  const [query, setQuery] = useState('');
  const term = terms.find((t) => t.number === semesterNumber);
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    const digits = q.replace(/[^0-9]/g, '');
    const out: string[] = [];
    for (const [code, course] of index.byCode) {
      const title = String(course.fullTitle || course.courseName || '').toLowerCase();
      if ((digits.length >= 3 && code.includes(digits)) || title.includes(q)) out.push(code);
      if (out.length >= 8) break;
    }
    return out;
  }, [query, index]);
  if (!term) return null;
  const tabs = [{ id: 'search' as const, label: 'Search' }, { id: 'lists' as const, label: 'Your lists' }];
  return (
    <PlannerOverlayShell anchor={anchor} label={`Add to ${term.name}`} onClose={close} width={380}>
      <div className="pr-6">
        <h3 className="text-[15px] font-bold leading-snug">Add to {term.name}</h3>
        <p className="mt-0.5 text-xs text-[var(--ss-text-muted)]">{term.credits} cr planned</p>
      </div>
      <div className="inline-flex self-start rounded-control bg-[var(--ss-surface-subtle)] p-0.5 ring-1 ring-inset ring-[var(--ss-border)]" role="tablist" aria-label="Add from">
        {tabs.map((t) => (
          <button key={t.id} type="button" role="tab" aria-selected={tab === t.id} aria-controls={`planner-add-${t.id}`} id={`planner-add-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`min-h-8 rounded-control px-3 text-xs font-semibold ${tab === t.id ? 'bg-[var(--ss-surface)] text-[var(--ss-text)] shadow-e1' : 'text-[var(--ss-text-muted)] hover:text-[var(--ss-text)]'}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'search' ? (
        <div id="planner-add-search" role="tabpanel" aria-labelledby="planner-add-tab-search" className="flex flex-col gap-2">
          <label className="flex items-center gap-2 rounded-control border border-[var(--ss-border)] px-2.5 py-1.5 focus-within:border-[var(--ss-brand)]">
            <MagnifyingGlassIcon className="h-4 w-4 text-[var(--ss-text-muted)]" aria-hidden="true" />
            <input data-autofocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Course title or number" className="w-full bg-transparent text-sm outline-none placeholder:text-[var(--ss-text-muted)]" aria-label="Search the catalog" />
          </label>
          {results.map((code) => {
            const where = placement.get(code);
            const done = completed.has(code);
            return (
              <div key={code} className="flex items-center justify-between gap-2 rounded-control border border-[var(--ss-border)] px-2.5 py-2">
                <span className="min-w-0">
                  <span className="block truncate text-[13px] font-semibold">{nameOf(code)}</span>
                  <span className="font-mono text-[11px] text-[var(--ss-text-muted)]">{shortCode(code)} · {creditsOf(code) || '?'} cr{done ? ' · earned' : where ? ` · in ${where.termName}` : ''}</span>
                </span>
                <button type="button" disabled={done || Boolean(where)} onClick={() => { actions.addCourse(semesterNumber, code); close(); }}
                  className="flex-none rounded-control border border-[var(--ss-border)] px-2.5 py-1 text-xs font-semibold hover:border-[var(--ss-brand)] hover:text-[var(--ss-brand)] disabled:opacity-50">
                  Add<span className="sr-only"> {nameOf(code)}</span>
                </button>
              </div>
            );
          })}
          {query.trim().length >= 2 && !results.length ? <p className="text-xs text-[var(--ss-text-muted)]">No catalog courses match.</p> : null}
          <button type="button" onClick={() => { actions.addFromSearch(semesterNumber); close(); }} className="self-start text-xs font-semibold text-[var(--ss-brand)] hover:underline">
            Open full search and requirement blocks
          </button>
        </div>
      ) : (
        <div id="planner-add-lists" role="tabpanel" aria-labelledby="planner-add-tab-lists" className="flex flex-col gap-2">
          <YourLists semesterNumber={semesterNumber} />
        </div>
      )}
    </PlannerOverlayShell>
  );
}

// ─── Issues ──────────────────────────────────────────────────────────────────────────────

export function IssuesPanel({ anchor }: { anchor: HTMLElement | null }) {
  const { issues, nameOf, placement, open, close } = usePlanner();
  return (
    <PlannerOverlayShell anchor={anchor} label="Plan issues" onClose={close} width={380}>
      <h3 className="pr-6 text-[15px] font-bold">{issues.length ? `${issues.length} thing${issues.length === 1 ? '' : 's'} to fix` : 'Nothing to fix'}</h3>
      {issues.map((issue, i) => {
        const code = normalize(issue.course);
        return (
          <div key={i} className="flex flex-col gap-1.5 rounded-control border border-[color-mix(in_srgb,var(--ss-critical)_30%,var(--ss-border))] bg-[var(--ss-critical-bg)] p-2.5">
            <span className="text-[13px] font-semibold">{nameOf(code)} <span className="font-mono text-[11px] font-normal text-[var(--ss-text-muted)]">{shortCode(code)}{placement.get(code) ? ` · ${placement.get(code)?.termName}` : ''}</span></span>
            <span className="text-xs">{issue.issue}</span>
            {issue.suggestion ? <span className="text-[11.5px] text-[var(--ss-text-muted)]">{issue.suggestion}</span> : null}
            <button type="button" data-planner-keep-overlay className="self-start text-xs font-semibold text-[var(--ss-brand)] hover:underline" onClick={(e) => open({ kind: 'course', code, anchor: e.currentTarget.ownerDocument.querySelector<HTMLElement>(`[data-plan-code="${code}"]`) })}>Review course</button>
          </div>
        );
      })}
      {!issues.length ? <p className="text-xs text-[var(--ss-text-muted)]">Prerequisite order and lab pairing look fine. These checks read catalog text and aren&apos;t an official audit.</p> : null}
    </PlannerOverlayShell>
  );
}

export function PlannerOverlayHost({ overlay, completionStorageKey, onCompletionClose }: { overlay: PlannerOverlay | null; completionStorageKey?: string; onCompletionClose?: () => void }) {
  const { close: closeCompletion } = usePlanner();
  if (!overlay) return null;
  if (overlay.kind === 'completion') return <CompletionOptions onClose={onCompletionClose || closeCompletion} storageKey={completionStorageKey} initialRequirementKey={overlay.requirementKey} />;
  if (overlay.kind === 'course') return <CoursePeek key={overlay.code} code={overlay.code} anchor={overlay.anchor} />;
  if (overlay.kind === 'list') return <ListPeek semesterNumber={overlay.semesterNumber} index={overlay.index} anchor={overlay.anchor} />;
  if (overlay.kind === 'slot') return <SlotResolver key={overlay.requirementKey} requirementKey={overlay.requirementKey} semesterNumber={overlay.semesterNumber} anchor={overlay.anchor} />;
  if (overlay.kind === 'move') return <MovePanel key={`${overlay.semesterNumber}-${overlay.index}`} semesterNumber={overlay.semesterNumber} index={overlay.index} anchor={overlay.anchor} />;
  if (overlay.kind === 'term-add') return <TermAddPanel key={overlay.semesterNumber} semesterNumber={overlay.semesterNumber} anchor={overlay.anchor} />;
  return <IssuesPanel anchor={overlay.anchor} />;
}
